import {
  Button,
  Loader,
  Modal,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@ax/ui';
import { VisitModalData } from './VisitFormModal';
import { useSoftDeleteAppointmentApi, useDeleteEHRAppointmentApi } from '@/hooks/api';
import toast from 'react-hot-toast';
import useAnalytics from '@/hooks/useAnalytics';
import { ACTIONS_CONSTANTS } from '@/constants/actions-constants';

interface VisitDeleteModalProps {
  modalOpen?: boolean;
  modalClose?: () => void;
  modalData?: VisitModalData;
  refetchData?: () => void;
}

const VisitDeleteModal: React.FC<VisitDeleteModalProps> = ({
  modalOpen = false,
  modalClose,
  modalData,
  refetchData,
}) => {
  const analytics = useAnalytics();
  const { mutateAsync: softDeleteEHRAppointment, isLoading: isDeletingEHRAppointment } =
    useDeleteEHRAppointmentApi();
  const { mutateAsync: softDeleteAppointment, isLoading: isDeletingAppointment } =
    useSoftDeleteAppointmentApi();

  const handleDeleteClick = async () => {
    let deleteEHRAppointmentResponse;
    try {
      if (modalData?.uuid) {
        deleteEHRAppointmentResponse = await softDeleteEHRAppointment({
          appointmentUuid: modalData?.uuid,
        });

        if (deleteEHRAppointmentResponse?.data?.success) {
          await softDeleteAppointment({
            appointmentId: modalData?.uuid,
            noteId: modalData?.uuid,
            deleted: true,
          });
          modalClose?.();
          refetchData?.();
          toast.success('Visit deleted successfully');

          sendMixpanelEvent(true);
        } else {
          toast.error('An error occurred, please try again');
        }
      } else {
        toast.error('An error occurred, please try again');
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred, please try again');
    }
  };

  const handleCancelClick = () => {
    modalClose?.();
    sendMixpanelEvent(false);
  };

  const sendMixpanelEvent = (isDeleting: boolean) => {
    const analyticsProperties = {
      eventDateTime: new Date()?.toString(),
      noteId: modalData?.uuid,
      action: isDeleting ? ACTIONS_CONSTANTS.delete : ACTIONS_CONSTANTS.cancel,
    };
    analytics.onVisitArchived(analyticsProperties);
  };

  const renderDeleteButtonContent = () => {
    return isDeletingEHRAppointment || isDeletingAppointment ? (
      <Loader
        color="#fff"
        text={'Deleting...'}
      />
    ) : (
      'Delete'
    );
  };

  return (
    <Modal onShow={modalOpen}>
      <ModalDialog>
        <ModalHeader onHide={() => modalClose?.()}>
          <ModalTitle>Please confirm</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-y-1">
            <p>Are you sure you want to delete this visit?</p>
            <p>Deleting the visit will also delete all associated transcripts and note content.</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            block
            variant="outline"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <Button
            block
            variant="primary"
            onClick={handleDeleteClick}
            disabled={isDeletingEHRAppointment || isDeletingAppointment}
          >
            {renderDeleteButtonContent()}
          </Button>
        </ModalFooter>
      </ModalDialog>
    </Modal>
  );
};

export default VisitDeleteModal;
